import { Elevation } from "./Elevation";

/**
 * The landing hero, ported from the approved design canvas.
 *
 * Everything that moves is CSS (hero.css) driven by timings in timeline.json.
 * This file contributes structure and copy only — no animation logic, and no
 * motion library.
 *
 * [YEAR] and [CITY] are deliberate placeholders. They stay until the studio
 * supplies the real values; inventing a founding year or a city on a page that
 * presents itself as a record of built work would be a lie in the product.
 */

/** The five stages of the scale bar, in order. */
const STAGES = [
  { label: "Vision", state: "active" },
  { label: "Drawing", state: "solid" },
  { label: "Engineering", state: "hollow" },
  { label: "Structure", state: "solid" },
  { label: "Legacy", state: "hollow" },
] as const;

/** The drawing's title block — a drafting sheet's identification strip. */
const TITLE_BLOCK = [
  { term: "Project no.", value: "01" },
  { term: "Sheet", value: "A — 001" },
  { term: "Scale", value: "1 : 100" },
  { term: "Location", value: "[CITY]" },
] as const;

function ArrowRight() {
  return (
    <svg width="24" height="12" viewBox="0 0 24 12" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">
      <path d="M0 6h22M17 1l5 5-5 5" />
    </svg>
  );
}

export function Hero() {
  return (
    <section id="top" className="iea-hero" aria-labelledby="hero-title">
      {/* Twelve-column drafting grid, behind everything. */}
      <div className="iea-hero__grid grid12" aria-hidden="true" />

      {/* Rotated studio line down the left edge. Desktop only. */}
      <p className="iea-hero__sidelabel" aria-hidden="true">
        <span className="intro-label" style={{ "--t": "var(--tl-eng)" } as React.CSSProperties}>
          <span className="iea-mono">Indraprastha Engineers &amp; Architects — [CITY], India</span>
        </span>
      </p>

      <div className="iea-hero__inner">
        {/* ------------------------------------------------ copy column */}
        <div className="iea-hero__copy">
          <div className="intro-up iea-hero__eyebrow" style={{ "--t": "var(--tl-draw)" } as React.CSSProperties}>
            <span className="iea-mono iea-hero__est">Est. [YEAR]</span>
            <span className="iea-hero__tick" aria-hidden="true" />
            <span className="iea-mono">Architecture / Engineering / Development</span>
          </div>

          <div className="iea-hero__lede">
            <h1 id="hero-title" className="iea-display iea-hero__title">
              <span className="type-line iea-hero__titleline">
                <span style={{ "--t": "var(--tl-we)" } as React.CSSProperties}>
                  <span className="iea-hero__kern">W</span>e build
                </span>
              </span>
              <span className="iea-hero__titleline">
                <span className="type-line iea-hero__titleinline">
                  <span style={{ "--t": "var(--tl-what)" } as React.CSSProperties}>what</span>
                </span>{" "}
                <em className="type-lasts iea-hero__lasts">lasts.</em>
              </span>
            </h1>

            <div className="iea-hero__subrow">
              <span
                className="intro-rule iea-hero__rule"
                style={{ "--t": "var(--tl-lead)" } as React.CSSProperties}
                aria-hidden="true"
              />
              <p
                className="intro-up iea-hero__sub"
                style={{ "--t": "calc(var(--tl-lead) + .1s)" } as React.CSSProperties}
              >
                Architecture, engineering and real estate — guided by precision.
              </p>
            </div>

            <div className="intro-up iea-hero__ctas" style={{ "--t": "var(--tl-cta)" } as React.CSSProperties}>
              <a href="#projects" className="iea-hero__cta">
                <span>Explore Projects</span>
                <ArrowRight />
              </a>
              <a href="#services" className="iea-hero__cta2">
                <span className="iea-hero__plus" aria-hidden="true">
                  +
                </span>
                <span className="iea-hero__cta2text">Our Capabilities</span>
              </a>
            </div>
          </div>

          {/* ------------------------------------------- scale bar */}
          <div className="iea-hero__scale">
            <div className="intro-seg-text iea-hero__scalehead">
              <span className="iea-mono iea-hero__scaletitle">From vision to legacy</span>
              <span className="iea-mono iea-hero__scalemeta">Scale · 5 stages</span>
            </div>
            <ol className="iea-hero__stages">
              {STAGES.map((stage, index) => (
                <li key={stage.label} className="iea-hero__stage">
                  <span
                    className={`iea-hero__seg ${stage.state === "active" ? "intro-seg-active" : "intro-seg"}`}
                    data-state={stage.state}
                    style={{ "--i": index } as React.CSSProperties}
                    aria-hidden="true"
                  />
                  <span
                    className="intro-seg-text iea-hero__stagelabel"
                    style={{ "--i": index } as React.CSSProperties}
                    data-state={stage.state}
                  >
                    {stage.label}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* ------------------------------------------------ drawing column */}
        <div className="iea-hero__art">
          {/* One frame for the labels and the drawing: the labels are placed as
              percentages of the 884-wide viewBox, so they have to resolve
              against the drawing's box rather than the column's. */}
          <div className="iea-hero__artframe">
            <span
              className="intro-label-art iea-hero__artlabel iea-hero__artlabel--draw"
              style={{ "--t": "calc(var(--tl-eng) + .3s)" } as React.CSSProperties}
            >
              <span className="iea-mono">Drawing</span>
            </span>
            <span
              className="intro-label-art iea-hero__artlabel iea-hero__artlabel--real"
              style={{ "--t": "var(--tl-real-label)" } as React.CSSProperties}
            >
              <span className="iea-mono">Reality</span>
            </span>

            <Elevation />
          </div>

          <div className="intro-titleblock iea-hero__titleblock">
            <div className="iea-hero__tbhead">
              <span className="iea-mono iea-hero__tbtitle">Form · Function · Structure</span>
              <span className="iea-mono iea-hero__tbmark">IEA</span>
            </div>
            <dl className="iea-hero__tbgrid">
              {TITLE_BLOCK.map((row) => (
                <div key={row.term} className="iea-hero__tbcell">
                  <dt className="iea-hero__tbterm">{row.term}</dt>
                  <dd className="iea-hero__tbvalue">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
